import { Box, Text, VStack } from '@chakra-ui/react';
import { BackofficeActivitiesOrderItem } from '@diamond/shared/types';
import { numberWithSeparator } from '@diamond/shared/utils';

import { useQuantityFormat } from '../../hooks/use-quantity-format';
import { ColumnComponentProps } from '../../types/activity-column-types';
import {
  FulfillmentStage,
  getActivityItemDisplay,
  isActivityDetailFulfillmentStageVisible,
  resolveFulfillmentStageQuantityInput,
  shouldShowFulfillmentStageQuantityPlaceholder,
} from '../../utils/activity-formatters';

const qtyTextProps = {
  fontSize: 'sm' as const,
  fontWeight: '500' as const,
  color: '#0941A8',
  textAlign: 'center' as const,
  w: 'full' as const,
  lineHeight: 'short' as const,
};

type DiffLabel = 'Pesanan Awal' | 'harga awal' | 'total awal';

type DiffLabelProps = {
  label: DiffLabel;
  value: string;
  align?: 'left' | 'center' | 'right';
};

export function ActivityDetailDiffLabel({
  label,
  value,
  align = 'center',
}: DiffLabelProps) {
  return (
    <Box color="gray.500" mt={1} textAlign={align} w="full">
      <Text fontSize="xs" fontWeight="400">
        {label}
      </Text>
      <Text fontSize="xs" fontWeight="semibold">
        {value}
      </Text>
    </Box>
  );
}

export function ActivityDetailStagePlaceholder() {
  return (
    <Text
      fontSize="sm"
      fontWeight="400"
      color="#373F41"
      textAlign="center"
      w="full"
    >
      -
    </Text>
  );
}

type Props = ColumnComponentProps<
  BackofficeActivitiesOrderItem,
  number | null | undefined
> & {
  value?: number | null;
  variant?: 'stage' | 'buyer-aligned';
  stage?: FulfillmentStage;
  orderStatus?: string;
};

function QuantityWithMultiplier({
  quantity,
  multiplier,
}: {
  quantity: number;
  multiplier: number | null;
}) {
  if (multiplier && multiplier !== 1) {
    return (
      <VStack spacing={0} align="center" w="full">
        <Text {...qtyTextProps}>{quantity}</Text>
        <Text
          color="gray.500"
          fontSize="xs"
          fontWeight="400"
          textAlign="center"
        >
          x {multiplier}
        </Text>
      </VStack>
    );
  }

  return <Text {...qtyTextProps}>{quantity}</Text>;
}

export function ActivityDetailStageQtyCell({
  info,
  value,
  variant = 'stage',
  stage,
  orderStatus,
}: Props) {
  const item = info.row.original;
  const display = getActivityItemDisplay(item);
  const stageQuantityInput = resolveFulfillmentStageQuantityInput(
    orderStatus,
    value,
    item.confirmed_quantity
  );
  const stageFormat = useQuantityFormat(item, stageQuantityInput);
  const buyerFormat = useQuantityFormat(
    item,
    item.confirmed_quantity ?? undefined
  );

  if (
    stage != null &&
    !isActivityDetailFulfillmentStageVisible(orderStatus, stage, item)
  ) {
    return <ActivityDetailStagePlaceholder />;
  }

  if (
    variant === 'stage' &&
    stage != null &&
    shouldShowFulfillmentStageQuantityPlaceholder(orderStatus, value)
  ) {
    return <ActivityDetailStagePlaceholder />;
  }

  if (variant === 'buyer-aligned') {
    return (
      <VStack spacing={0} align="center" w="full">
        <QuantityWithMultiplier
          quantity={buyerFormat.quantity}
          multiplier={buyerFormat.multiplier}
        />
        {display.showInitialQuantity ? (
          <ActivityDetailDiffLabel
            label="Pesanan Awal"
            value={numberWithSeparator(display.checkoutQuantity)}
          />
        ) : null}
      </VStack>
    );
  }

  return (
    <QuantityWithMultiplier
      quantity={stageFormat.quantity}
      multiplier={stageFormat.multiplier}
    />
  );
}
